import { useEffect, useState } from 'react'
import teamService from '../../services/teamService'
import { ROLE_LABELS } from '../../services/userService'
import { HiOutlinePlus, HiOutlineX, HiOutlinePencil, HiOutlineTrash, HiOutlineUserGroup } from 'react-icons/hi'
import toast from 'react-hot-toast'

function TeamManagement() {
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // team | 'new' | null

  const load = async () => {
    setLoading(true)
    try {
      const res = await teamService.list()
      if (res.status === 'success') setTeams(res.data)
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const remove = async (t) => {
    if (!window.confirm(`Xóa nhóm "${t.name}"? (Thành viên sẽ về trạng thái chưa có nhóm)`)) return
    try { await teamService.remove(t.id); toast.success('Đã xóa nhóm'); load() } catch { /* interceptor */ }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Nhóm nhân sự</h1>
          <p className="page-subtitle">Tạo nhóm để trưởng nhóm phân luồng lead cho thành viên</p>
        </div>
        <button onClick={() => setEditing('new')} className="btn-primary"><HiOutlinePlus className="w-5 h-5" /> Tạo nhóm</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
      ) : teams.length === 0 ? (
        <div className="card p-12 text-center text-secondary-400">Chưa có nhóm nào. Bấm “Tạo nhóm”.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((t) => (
            <div key={t.id} className="card p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center"><HiOutlineUserGroup className="w-5 h-5" /></div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-secondary-900 truncate">{t.name}</h3>
                    <div className="text-2xs text-secondary-400">{t.member_count} thành viên</div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditing(t)} className="p-1.5 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded"><HiOutlinePencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(t)} className="p-1.5 text-secondary-500 hover:text-red-600 hover:bg-red-50 rounded"><HiOutlineTrash className="w-4 h-4" /></button>
                </div>
              </div>
              {t.description && <p className="text-sm text-secondary-500 mt-2">{t.description}</p>}
              {t.members?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-secondary-50 space-y-1">
                  {t.members.map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-sm">
                      <span className="text-secondary-700">{m.full_name}</span>
                      <span className="text-2xs text-secondary-400">{ROLE_LABELS[m.role] || m.role}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TeamModal team={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} />
      )}
      <p className="text-2xs text-secondary-400 mt-4">* Gán thành viên vào nhóm tại trang <b>Phân quyền</b> (chọn Nhóm khi tạo/sửa tài khoản).</p>
    </div>
  )
}

function TeamModal({ team, onClose, onSaved }) {
  const isNew = !team
  const [form, setForm] = useState({ name: team?.name || '', description: team?.description || '', is_active: team?.is_active ?? true })
  const [saving, setSaving] = useState(false)
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }))

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nhập tên nhóm'); return }
    setSaving(true)
    try {
      if (isNew) { await teamService.create(form); toast.success('Đã tạo nhóm') }
      else { await teamService.update(team.id, form); toast.success('Đã cập nhật') }
      onSaved()
    } catch { /* interceptor */ } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative card w-full max-w-md p-6 animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-secondary-900">{isNew ? 'Tạo nhóm' : 'Sửa nhóm'}</h3>
          <button onClick={onClose} className="p-1.5 text-secondary-400 hover:text-secondary-700"><HiOutlineX className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label">Tên nhóm <span className="text-red-500">*</span></label>
            <input className="input" value={form.name} onChange={(e) => setF('name', e.target.value)} placeholder="VD: Nhóm Tư vấn 1" />
          </div>
          <div>
            <label className="label">Mô tả</label>
            <input className="input" value={form.description} onChange={(e) => setF('description', e.target.value)} />
          </div>
          <button onClick={save} disabled={saving} className="btn-primary w-full mt-1">{saving ? 'Đang lưu...' : (isNew ? 'Tạo nhóm' : 'Lưu thay đổi')}</button>
        </div>
      </div>
    </div>
  )
}

export default TeamManagement
